import { useState, useRef, useEffect } from "react";
import { Check, X, Pencil } from "lucide-react";

interface EditableTextFieldProps {
  value: string;
  onSave: (value: string) => void;
  type?: "text" | "number" | "url";
  className?: string;
  displayClassName?: string;
  placeholder?: string;
}

export function EditableTextField({
  value,
  onSave,
  type = "text",
  className = "",
  displayClassName = "",
  placeholder = "",
}: EditableTextFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    const handleScroll = () => {
      if (isEditing) {
        // Save current value before closing
        if (tempValue.trim() !== "" && tempValue !== value) {
          onSave(tempValue);
        }
        setIsEditing(false);
      }
    };

    if (isEditing) {
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isEditing, tempValue, value, onSave]);

  const handleSave = () => {
    if (tempValue.trim() !== "" && tempValue !== value) {
      onSave(tempValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type={type}
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={`px-2 py-1 bg-[var(--muted)] text-[var(--foreground)] border border-[var(--color-steel-dark)] rounded focus:outline-none focus:ring-2 focus:ring-[var(--color-steel-dark)] ${className}`}
          placeholder={placeholder}
          step={type === "number" ? "0.01" : undefined}
          min={type === "number" ? "0" : undefined}
        />
      </div>
    );
  }

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        setIsEditing(true);
      }}
      className={`cursor-pointer hover:bg-[var(--color-steel-dark)]/10 rounded px-2 py-1 transition-colors group inline-flex items-center ${displayClassName}`}
      title="Cliquer pour modifier"
    >
      <span>{value}</span>
    </div>
  );
}

interface EditableSelectFieldProps {
  value: string;
  options: string[];
  onSave: (value: string) => void;
  className?: string;
  displayClassName?: string;
  renderDisplay?: (value: string) => React.ReactNode;
  renderOption?: (value: string) => React.ReactNode;
}

export function EditableSelectField({
  value,
  options,
  onSave,
  className = "",
  displayClassName = "",
  renderDisplay,
  renderOption,
}: EditableSelectFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditing && selectRef.current) {
      selectRef.current.focus();
      
      // Detect position and determine if dropdown should appear above or below
      const rect = selectRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 250; // Approximate height of dropdown
      const dropdownWidth = 200; // Approximate width of dropdown
      
      // Determine vertical position
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
      
      // Calculate horizontal position
      let leftPosition = rect.left;
      const spaceRight = window.innerWidth - rect.left;
      
      // If dropdown would overflow on the right, adjust position
      if (spaceRight < dropdownWidth) {
        // Align to the right edge of the viewport with some padding
        leftPosition = window.innerWidth - dropdownWidth - 16;
      }
      
      // Ensure it doesn't go off the left edge either
      if (leftPosition < 16) {
        leftPosition = 16;
      }
      
      // Build the style object
      const style: React.CSSProperties = {
        left: `${leftPosition}px`,
      };
      
      if (dropdownPosition === 'bottom') {
        style.top = `${rect.bottom + 8}px`;
      } else {
        style.bottom = `${window.innerHeight - rect.top + 8}px`;
      }
      
      setDropdownStyle(style);
    }
  }, [isEditing, dropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsEditing(false);
      }
    };

    const handleScroll = () => {
      setIsEditing(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsEditing(false);
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true); // Use capture phase to catch all scroll events
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing]);

  const handleChange = (newValue: string) => {
    if (newValue !== value) {
      onSave(newValue);
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div ref={selectRef} className="relative" onClick={(e) => e.stopPropagation()}>
        {/* Current selected value displayed as pill */}
        <div className={`${displayClassName} ${className}`}>
          <span>{renderDisplay ? renderDisplay(value) : value}</span>
        </div>
        
        {/* Dropdown menu with other options */}
        <div 
          className={`fixed z-[100] bg-[var(--muted)] border border-[var(--color-steel-dark)] rounded-lg shadow-xl p-2 w-[200px] max-w-[calc(100vw-32px)] max-h-[250px] overflow-y-auto animate-in fade-in duration-200 ${
            dropdownPosition === 'top' ? 'slide-in-from-bottom-2' : 'slide-in-from-top-2'
          }`}
          style={dropdownStyle}
        >
          <div className="flex flex-col gap-1.5">
            {options.map((option) => (
              <button
                key={option}
                onClick={() => handleChange(option)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all text-left whitespace-nowrap overflow-hidden text-ellipsis ${
                  option === value
                    ? 'bg-[var(--color-steel-dark)]/30 text-[var(--color-steel)] border border-[var(--color-steel-dark)]/50 cursor-default'
                    : 'bg-[var(--color-steel-dark)]/20 text-[var(--color-steel)] border border-[var(--color-steel-dark)]/40 hover:bg-[var(--color-steel-dark)]/40 hover:border-[var(--color-steel-dark)]/60 cursor-pointer'
                }`}
              >
                {renderOption ? renderOption(option) : option}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        setIsEditing(true);
      }}
      className={`cursor-pointer hover:bg-[var(--color-steel-dark)]/10 rounded px-2 py-1 transition-colors group inline-flex items-center ${displayClassName}`}
      title="Cliquer pour modifier"
    >
      <span>{renderDisplay ? renderDisplay(value) : value}</span>
    </div>
  );
}

interface EditableImageFieldProps {
  value: string;
  itemName: string;
  onSave: (value: string) => void;
}

export function EditableImageField({ value, itemName, onSave }: EditableImageFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  const handleSave = () => {
    if (tempValue.trim() !== "" && tempValue !== value) {
      onSave(tempValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <img src={tempValue} alt={itemName} className="w-12 h-12 rounded-lg object-cover shadow-sm" />
        <input
          type="url"
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") handleCancel();
          }}
          className="w-full px-2 py-1 text-xs border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="URL de l'image"
          autoFocus
        />
        <div className="flex gap-1">
          <button
            onClick={handleSave}
            className="p-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
          >
            <Check className="w-3 h-3" />
          </button>
          <button
            onClick={handleCancel}
            className="p-1 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className="cursor-pointer hover:ring-2 hover:ring-blue-500 rounded-lg transition-all group relative"
      title="Cliquer pour modifier l'image"
    >
      <img src={value} alt={itemName} className="w-12 h-12 rounded-lg object-cover shadow-sm" />
      <div className="absolute inset-0 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <Pencil className="w-4 h-4 text-white" />
      </div>
    </div>
  );
}

interface EditableNumberFieldProps {
  value: number;
  onSave: (value: number) => void;
  prefix?: string;
  suffix?: string;
  className?: string;
  displayClassName?: string;
  decimals?: number; // Number of decimal places to display
}

export function EditableNumberField({
  value,
  onSave,
  prefix = "",
  suffix = "",
  className = "",
  displayClassName = "",
  decimals = 2,
}: EditableNumberFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    const handleScroll = () => {
      if (isEditing) {
        // Save current value before closing
        const numValue = parseFloat(tempValue);
        if (!isNaN(numValue) && numValue !== value) {
          onSave(numValue);
        }
        setIsEditing(false);
      }
    };

    if (isEditing) {
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isEditing, tempValue, value, onSave]);

  const handleSave = () => {
    const numValue = parseFloat(tempValue);
    if (!isNaN(numValue) && numValue !== value) {
      onSave(numValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempValue(String(value));
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {prefix && <span className="text-[var(--muted-foreground)]">{prefix}</span>}
        <input
          ref={inputRef}
          type="number"
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={`w-24 px-2 py-1 bg-[var(--muted)] text-[var(--foreground)] border border-[var(--color-steel-dark)] rounded focus:outline-none focus:ring-2 focus:ring-[var(--color-steel-dark)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${className}`}
          step={decimals > 0 ? "0.01" : "1"}
          min="0"
        />
        {suffix && <span className="text-[var(--muted-foreground)]">{suffix}</span>}
      </div>
    );
  }

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        setIsEditing(true);
      }}
      className={`cursor-pointer hover:bg-[var(--color-steel-dark)]/10 rounded px-2 py-1 transition-colors group flex items-center gap-2 ${displayClassName}`}
      title="Cliquer pour modifier"
    >
      <span>
        {prefix}
        {decimals > 0 ? value.toFixed(decimals) : Math.round(value)}
        {suffix}
      </span>
    </div>
  );
}